import {Box, useTheme} from '@chakra-ui/react'
import isEmpty from 'lodash/isEmpty'
import {useEffect, useRef, useState} from 'react'
import ContentEditable from 'react-contenteditable'
import sanitizeHtml from 'sanitize-html'
import styles from '../../../styles/modules/textEditor.module.scss'
import {SELECTED_GENERATION_OPTION} from '../../../constants/playground'
import useTranslation from 'next-translate/useTranslation'
import {useSkill} from '../../../hooks/useSkill'

interface Props {
  input: string
  output: string
  setInput: (input: string) => void
  setOutput: (output: string) => void
  setSelectedGenerationType: (selectedGenerationType: SELECTED_GENERATION_OPTION) => void
}

const TextInput = ({input, output, setInput, setOutput, setSelectedGenerationType}: Props) => {
  const ref = useRef(null)
  const theme = useTheme()

  const {t} = useTranslation('common')

  const [currentInnerText, setCurrentInnerText] = useState<string>('')
  const [html, setHtml] = useState<string>(
    `${input}${
      !isEmpty(output) ? `<span style="color:${theme.colors.brand.primary}">${output}</span>` : ''
    }`
  )

  const {isToxic, setIsToxic, isLoading} = useSkill()

  useEffect(() => {
    // Check if input has been modified from parent
    if (input !== currentInnerText || !isEmpty(output)) {
      setHtml(
        `${input}${
          !isEmpty(output.toString())
            ? `<span style="color:${
                isToxic ? theme.colors.brand.error : theme.colors.brand.primary
              }">${output}</span>`
            : ''
        }`
      )
      setCurrentInnerText(input)
      updateScroll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, output, isToxic])

  useEffect(() => {
    if (isEmpty(output)) {
      setHtml(input)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [output])

  const resetEditor = () => {
    setHtml('')
    setInput('')
    setOutput('')
    setCurrentInnerText('')
    setIsToxic(false)
  }

  function updateScroll() {
    // @ts-ignore
    ref.current.scrollTop = ref.current.scrollHeight
  }

  const handleTextChange = (event: any) => {
    if (isEmpty(event.currentTarget?.innerText)) return resetEditor()

    if (!isToxic) setIsToxic(false)

    const innerText = event.currentTarget.innerText

    if (innerText.trim().length != 0) {
      setHtml(clean(event.currentTarget.innerHTML))
      setCurrentInnerText(innerText)
      setInput(innerText)
      setOutput('')
      setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FIRST)
    } else {
      resetEditor()
    }
  }

  const clean = (dirtyHtml: string) =>
    sanitizeHtml(dirtyHtml, {
      allowedTags: ['div', 'br'],
    })?.replaceAll('<br />', '<br>')

  return (
    <Box
      overflowY='auto'
      flex={1}
      w='full'
      ref={ref}
      alignItems='start'
      color={isToxic ? theme.colors.brand.error : theme.colors.text.basic}
      className='hide-scrollbar'>
      <ContentEditable
        html={html}
        disabled={isLoading}
        className={styles.richTextArea}
        placeholder={t('text_editor_placeholder')}
        onChange={handleTextChange}
        onFocus={handleTextChange}
      />
    </Box>
  )
}

export default TextInput
